import { TFunction } from 'i18next';
import { I2DCADDocuments, ISWGDocuments } from '../model/IGetAllCadFileParams';
import { getOptionForValue } from './util';
import { IDocumentListItemProps } from '../model/IGetAllFileDataParams';
import { FileData } from '../reducers/fileState';
import { docTypes } from './constant';
import vehicleCodes from '../assets/translations/en-gb/vehicle-codes.json';
import { Option } from '../model/IFormModel';
import {
  DOWNLOAD_METADATA_TO_REPLACE,
  SHORT_DOWNLOAD_METADATA_TO_REPLACE,
  SHORT_DOWNLOAD_CAD_METADATA_TO_REPLACE,
} from '../analytics/analytics.constants';

export const NA_LANGUAGES: string[] = ['en-us', 'en-ca', 'fr-ca'];
export const EU_LANGUAGES: string[] = [
  'en-gb',
  'nl-nl',
  'nl-be',
  'es-es',
  'de-de',
  'fr-fr',
  'fr-be',
  'pl-pl',
  'cz-cz',
  'da-dk',
  'fi-fi',
  'el-gr',
  'hu-hu',
  'it-it',
  'sv-se',
  'nb-no',
  'pt-pt',
  'tr-tr',
  'ru-ru',
  'en-ie',
  'de-at',
  'fr-ch',
  'de-ch',
  'it-ch',
  'no-no',
  'cs-cz',
  'ro-ro',
];

// these are EU fields that need to be converted from vehicles codes to labels
// must match exactly the fields in the api response,  used in props & defined in vehicle-codes
export const EU_FIELDS_TO_CONVERT = [
  'nameplate',
  'bodystyle',
  'modelYear', // modelYear needs translation for French languages
  'wheelbase',
  'drive',
  'roof',
  'rearWheels',
  'rearDoors',
];

export const ALL_LANGUAGES = [...EU_LANGUAGES, ...NA_LANGUAGES];

export const isEULanguage = (lang: string) => EU_LANGUAGES.includes(lang);

export const isNALanguage = (lang: string) => NA_LANGUAGES.includes(lang);

export const convertLanguage = (language: string): string => {
  if (language.includes('-')) {
    language = language.replace('-', '_');
  }
  const [prefix, suffix] = language.split('_'); // language from FPG
  return `${prefix}_${suffix.toUpperCase()}`;
};

export const getCountry = (locale: string) => locale.split('-')[1];

export function getEUTranslatedResponse<
  T extends I2DCADDocuments | IDocumentListItemProps | FileData | ISWGDocuments,
>(docData: T, tFunc: TFunction): T {
  // don't mutate the incoming param
  const resultDoc = { ...docData };

  for (const field of EU_FIELDS_TO_CONVERT) {
    const opt = getOptionForValue(docData[field], tFunc(field, { returnObjects: true }));
    resultDoc[field] = opt ? opt.label : docData[field];
  }
  return resultDoc;
}

const localeMap = {
  // These are the languages that differ between FPG and special vehicles & these are captured from application properties
  'nl-be': 'nl-nl',
  'fr-be': 'fr-fr',
  'cz-cz': 'cs-cz',
  'nb-no': 'no-no',
  'en-ie': 'en-gb',
};

export const isSameLanguage = (code1: string, code2: string) => {
  return normalizeCode(code1) === normalizeCode(code2);
};
const normalizeCode = (code: string) => {
  /* docs for respective markets are fetched based on localeMap from backend.
  eg.'nb-no' is returned as 'no-no' because the language codes differ between FPG and special vehicles. */
  const newCode = code.replace('_', '-').toLowerCase();
  return localeMap[newCode] || newCode;
};

export const EU9_LANGUAGES: string[] = [
  'tr-tr',
  'fi-fi',
  'hu-hu',
  'da-dk',
  'sv-se',
  'pt-pt',
  'el-gr',
  'ro-ro',
  'cs-cz',
];

export const getDownloadNameForAnalytics = (
  documentType: string,
  docData: IDocumentListItemProps | I2DCADDocuments | FileData,
) => {
  const doctype =
    documentType.toLowerCase() === 'bemm' ? 'bemm' : docTypes[documentType].toLowerCase();

  // don't mutate the incoming object
  const expandedDocData = { ...docData };

  translateEUFields(docData, expandedDocData);

  // either english name or title
  const analyticsTitle = docData.englishTitle ?? docData.title;
  const downloadNameWithMetadata = DOWNLOAD_METADATA_TO_REPLACE.replace(
    '<title>',
    `${analyticsTitle ?? expandedDocData.fileName}`, // title prop may not present in CAD docs
  )
    .replace('<document_type>', `${doctype}`)
    .replace('<nameplate>', `${expandedDocData.nameplate}`)
    .replace('<language>', `${expandedDocData.language ?? ''}`)
    .replace('<year>', `${expandedDocData.modelYear ?? docData.year ?? ''}`)
    .replace('<quarter>', `${expandedDocData.quarter ?? ''}`)
    .replace('<body_style>', `${expandedDocData.bodystyle ?? ''}`)
    .replace('<wheel_base>', `${expandedDocData.wheelbase ?? ''}`)
    .replace('<drive_type>', `${expandedDocData.drive ?? ''}`)
    .replace('<rear_wheels>', `${expandedDocData.rearWheels ?? ''}`)
    .replace('<roof_height>', `${expandedDocData.roof ?? ''}`)
    .replace('<rear_doors>', `${expandedDocData.rearDoors ?? ''}`)
    .replace('<category>', `${expandedDocData.category ?? ''}`);

  return downloadNameWithMetadata;
};

// Shortened download name for EU analytics
export const getShortDownloadNameForAnalytics = (
  documentType: string,
  docData: IDocumentListItemProps | I2DCADDocuments | FileData,
) => {
  const expandedDocData = { ...docData };

  if (documentType === 'SWG') {
    translateEUFields(docData, expandedDocData);
    return SHORT_DOWNLOAD_CAD_METADATA_TO_REPLACE.replace(
      '<title>',
      `${docData.title ?? expandedDocData.fileName}`,
    )
      .replace('<year>', `${expandedDocData.modelYear ?? docData.year ?? ''}`)
      .replace('<nameplate>', `${expandedDocData.nameplate}`)
      .replace('<body_style>', `${expandedDocData.bodystyle ?? ''}`)
      .replace('<wheel_base>', `${expandedDocData.wheelbase ?? ''}`);
  } else {
    const analyticsTitle = docData.englishTitle ?? docData.title;
    return SHORT_DOWNLOAD_METADATA_TO_REPLACE.replace(
      '<title>',
      `${analyticsTitle ?? expandedDocData.fileName}`,
    )
      .replace(
        '<year>',
        documentType === 'NEWSLETTER'
          ? `${expandedDocData.modelYear ?? docData.year ?? ''}|${expandedDocData.quarter ?? ''}` // add quarter for newsletters
          : `${expandedDocData.modelYear ?? docData.year ?? ''}`,
      )
      .replace('<language>', `${expandedDocData.language ?? ''}`);
  }
};

const translateEUFields = (
  docData: IDocumentListItemProps | I2DCADDocuments | FileData,
  expandedDocData: IDocumentListItemProps | I2DCADDocuments | FileData,
) => {
  // skip modelYear, does not need translation
  // OR, may be add modelYear to en-gb vehicleCodes json to remove this skipping
  for (const field of EU_FIELDS_TO_CONVERT.filter((f) => f !== 'modelYear')) {
    if (field in docData) {
      const searchOptionArr = vehicleCodes[field] as Option[];
      if (docData[field] instanceof Array) {
        expandedDocData[field] = docData[field]
          .map((each: string) => getOptionForValue(each, searchOptionArr))
          .map((opt: Option) => opt?.label ?? '')
          .join(','); // convert to comma separated value
      } else {
        const opt = getOptionForValue(docData[field], searchOptionArr);
        expandedDocData[field] = opt?.label ?? '';
      }
    }
  }
};
